import { useState, useMemo } from 'react';
import { UserRole } from '../types/shared';
import { NavItem } from '../types/navigation';
import { 
  getNavigationForRole, 
  hasRouteAccess, 
  getBreadcrumbs, 
  getRequiredRole 
} from '../utils/navigation';

// Define the return type for the hook
interface UseNavigationReturn {
  navigation: NavItem[];
  currentRoute: string;
  isAuthorized: boolean;
  requiredRole?: UserRole;
  breadcrumbs: { label: string; href: string }[];
  setCurrentRoute: (route: string) => void;
}

export function useNavigation(initialRole: UserRole, initialRoute: string = '/'): UseNavigationReturn {
  const [currentRole, setCurrentRole] = useState<UserRole>(initialRole);
  const [currentRoute, setCurrentRoute] = useState<string>(initialRoute);

  // Generate navigation based on user role
  const navigation = useMemo(() => 
    getNavigationForRole(currentRole), 
    [currentRole]
  );

  // Determine required role for the current route
  const requiredRole = useMemo(() => {
    const role = getRequiredRole(currentRoute);
    // Default to 'agent' if no specific role is found
    return role || 'agent';
  }, [currentRoute]);

  // Check route access
  const isAuthorized = useMemo(() => 
    hasRouteAccess(currentRole, currentRoute), 
    [currentRole, currentRoute]
  );

  // Generate breadcrumbs
  const breadcrumbs = useMemo(() => {
    const routeBreadcrumbs = getBreadcrumbs(currentRoute);
    return routeBreadcrumbs.map(crumb => ({
      label: crumb.label,
      href: crumb.path
    }));
  }, [currentRoute]);

  // Find current navigation item
  const currentItem = useMemo(() => 
    navigation.find(item => 
      item.path === currentRoute || 
      (item.children?.some(child => child.path === currentRoute))
    ), 
    [navigation, currentRoute]
  );

  return {
    navigation,
    currentRoute,
    isAuthorized,
    requiredRole,
    breadcrumbs,
    setCurrentRoute
  };
}

export default useNavigation;
